<template>
    <div>
        <div class="form-group">
            <div class="row">
                <div class="col-8">
                    <journey-dropdown v-model="journeyId" />
                </div>
                <div class="col-4">
                    <a class="btn btn-outline-secondary" :href="journeyUrl">
                        <i class="fas fa-route"></i> {{resource('JourneyBuilder_Overview_Composer_Button_Text')}}
                    </a>
                </div>
            </div>
        </div>
        
        <div class="form-group">
            <div class="row">
                <div class="col-12">
                    <div class="d-inline-block mr-3">
                        <div>
                            {{resource('Bool_Collapse')}}
                        </div>
                        <div>
                            <alter-amount-button :total="-10"  :disabled="!canDecreaseDisplayedTouchPoints" @alter-amount="incrementDisplayedTouchPointCount" />
                            <alter-amount-button :total="-1"   :disabled="!canDecreaseDisplayedTouchPoints" @alter-amount="incrementDisplayedTouchPointCount" />
                        </div>
                    </div>

                    <div class="d-inline-block">
                        <div>
                            {{resource('Bool_Expand')}}
                        </div>
                        <div>
                            <alter-amount-button :total="1"    :disabled="!canIncreaseDisplayedTouchPoints" @alter-amount="incrementDisplayedTouchPointCount" />
                            <alter-amount-button :total="10"   :disabled="!canIncreaseDisplayedTouchPoints" @alter-amount="incrementDisplayedTouchPointCount" />
                        </div>
                    </div>
                </div>
            </div>
        </div>
        
        <div class="form-group">
            <div class="row">
                <div class="col-12">
                    <diagram v-if="!isLoading" 
                            :data="displayedTouchPoints" 
                            :types="sankeyTypes"
                             
                             @nodeClick="nodeClick"
                    />
                    <loading-box v-else/>
                </div>
            </div>
        </div>

        <modal v-if="brickId"
               size="large"
               @hidden="brickId = null" ref="brickInfoPopup"
        >
            <template v-slot:modal-body>
                <touchpoint-statistics :brick-id="brickId" :is-sub-menu="false" />
            </template>

            <template v-slot:modal-footer>
                <div class="row justify-content-end">
                    <div class="col-auto">
                        <a class="btn btn-outline-secondary btn-lg" :href="journeyUrl">
                            <i class="fas fa-route"></i> {{resource('JourneyBuilder_Overview_Composer_Button_Text')}}
                        </a>

                        <button class="btn btn-secondary btn-lg" @click="brickId = null">
                            <i class="fas fa-check-circle"/>&nbsp;&nbsp;{{resource('Button_OK')}}
                        </button>
                    </div>
                </div>
            </template>
        </modal>
    </div>
</template>

<script>
    import api from '../../api';
    
    import guidMixin from '../../../../Util/Mixins/guid';
    import sankeyMixin from '../../Mixins/sankey-diagram';
    import resourceMixin from "../../../../Util/Mixins/resources";

    import JourneyDropdown from "./../JourneyDropdown.vue";
    import LoadingBox from "../../../../Components/Util/LoadingBox.vue";
    import Modal from "../../../../Components/Util/Modal.vue";
    import TouchpointStatistics from "../Composer/TouchpointOverview/Statistics/TouchpointStatistics.vue";

    const DEFAULT_DISPLAYED_TOUCHPOINTS = 3;
    const MINIMUM_DISPLAYED_TOUCHPOINTS = 1;

    const alterAmountButton = {
        render(createElement){
            const vm = this;
            
            return createElement('button', {
                class: 'btn btn-default',
                on: {
                    click: () => vm.$emit('alter-amount', vm.total)
                } 
            }, [vm.text]);
        },
        computed: {
            text(){
                //- is already included in stringified version
                return `${this.total > 0 ? '+' : ''}${this.total}`;
            }
        },
        props: {
            total: Number
        }
    };
    
    export default {
        components: {
            JourneyDropdown, 
            AlterAmountButton: alterAmountButton,
            LoadingBox,
            Modal,
            TouchpointStatistics
        },
        mixins: [sankeyMixin, resourceMixin, guidMixin],
        created() {
            this.isLoading = true;
            
            this.highestDisplayedTouchPointLevel = DEFAULT_DISPLAYED_TOUCHPOINTS;
        },
        methods: {
            incrementDisplayedTouchPointCount(amount){
                //Ensure we don't go below min/above max amount of tochpoints
                this.highestDisplayedTouchPointLevel = Math.min(
                    Math.max(MINIMUM_DISPLAYED_TOUCHPOINTS, this.highestDisplayedTouchPointLevel + amount),
                    this.maxDisplayedTouchPoints
                );
            },
            nodeClick(e){
                this.brickId = e.detail.brickId;
            }
        },
        watch: {
            journeyId(){
                //New journey selected; clearly mark us as loading
                this.isLoading = true;

                //Revert back to default amount on journey change
                this.highestDisplayedTouchPointLevel = DEFAULT_DISPLAYED_TOUCHPOINTS;
            },
            displayedTouchPoints(){
                //New journey data is guaranteed to be loaded
                this.isLoading = false;
            },
            'touchPointNodeMeta.maxLevel'(maxLevel){
                //We should not attempt to calculate our maxLevel when we have no data
                if(this.sankeyData.length === 0){
                    return;
                }
                
                //Ensure highestDisplayedTouchPointLevel never exceeds our maxLevel
                this.highestDisplayedTouchPointLevel = Math.min(this.highestDisplayedTouchPointLevel, maxLevel);
            },
            brickId(value){
                const vm = this;

                if(!value){
                    return;
                }

                //Wait for our modal to render prior to invoking the show function
                vm.$nextTick(() => {
                    vm.$refs.brickInfoPopup.show();
                });
            }
        },
        computed: {
            maxDisplayedTouchPoints(){
                return Math.max(this.touchPointNodeMeta.maxLevel, MINIMUM_DISPLAYED_TOUCHPOINTS);
            },
            touchPointNodeMeta(){
                const nodes = this.sankeyData;

                const nodeLookup = nodes.reduce((nodeLookup, node) => {
                    nodeLookup[node.id] = node;
                    return nodeLookup;
                }, {});

                const createTree = () => {
                    const _leafLookup = {};

                    const _createLeaf = (node) => {
                        //Create a shallow copy to allow safe modification of node properties
                        node = Object.assign({}, node);
                        
                        let _parent = undefined;
                        
                        const _directChildLookup = {};
                        const _goalChildren = {};
                        
                        let _proxyLeaf = undefined;
                        
                        return {
                            node: node,
                            children: [],
                            upcomingGoals: [],
                            level: 0,
                            get parent(){
                                return _parent;
                            },
                            set parent(parent){
                                this.node.from = parent.node.id;
                                
                                _parent = parent;
                                
                                //Leaves are added moving from top -> bottom
                                //this means the moment a parent is known, we know its level
                                this.level = this.parent.level + 1;
                                
                                parent._addChild(this);

                                if(this.node.isGoal){
                                    this._addGoal(this);
                                }
                            },
                            proxyTo(leaf){
                                if(_proxyLeaf){
                                    return;
                                }
                                
                                _proxyLeaf = leaf;
                                _proxyLeaf.node.total += this.node.total;
                                
                                //Move any existing children to proxyLeaf
                                this.children.forEach(child => child.parent = _proxyLeaf);
                            },
                            _addChild(child){
                                if(_proxyLeaf){
                                    child.parent = _proxyLeaf;
                                    return;
                                }
                                
                                if(_directChildLookup[child.node.id]){
                                    return;
                                }

                                _directChildLookup[child.node.id] = true;
                                this.children.push(child);
                            },
                            _addGoal(child){
                                if(_goalChildren[child.node.id]){
                                    return;
                                }

                                _goalChildren[child.node.id] = true;
                                this.upcomingGoals.push(child);
                                if(this.parent){
                                    this.parent._addGoal(child);
                                }
                            }
                        };
                    };

                    return {
                        root: undefined,
                        maxLevel: 0,
                        registerNode(node) {
                            if(_leafLookup[node.id]) {
                                return _leafLookup[node.id];
                            }

                            const leaf = _createLeaf(node);
                            _leafLookup[node.id] = leaf;

                            if(node.from) {
                                //Create parent node/assign add our current leaf as a child
                                leaf.parent = this.registerNode(nodeLookup[node.from]);
                            }
                            else{
                                //First encountered parentless node is root
                                //the rest gets it's total added to the root node + children appended to root
                                if(!this.root){
                                    this.root = leaf;
                                }
                                else{
                                    leaf.proxyTo(this.root);
                                }
                            }

                            this.maxLevel = Math.max(leaf.level, this.maxLevel);
                            
                            return leaf;
                        }
                    }
                };

                const tree = createTree();
                nodes.forEach(n => tree.registerNode(n));
                return { root: tree.root, maxLevel: tree.maxLevel };
            },
            displayedTouchPoints(){
                const vm = this;

                const {
                    touchPointNodeMeta: { root = undefined },
                    highestDisplayedTouchPointLevel: highestDisplayedLevel
                } = vm;
                
                const map = [];
                
                if(!root){
                    return map;
                }
                
                //Goals should always be displayed, no exceptions
                //when there is room, 'regular' touchpoints should be displayed
                //from left -> right (this can be based on level)
                //determining whether a regular touchpoint should be displayed
                //can be done by looking at the amount of goals after the given touchpoint
                //and adding that to the touchpoint's level
                
                
                const canDisplay = (leaf) => {
                    //Goals are always displayed
                    if(leaf.node.isGoal){
                        return true;
                    }
                    
                    //Leaf level + amount of distinct upcoming goal levels is the 'actual' level of a leaf
                    const totalUpcomingGoalLevelsSet = leaf.upcomingGoals.reduce((totalUpcomingGoalLevelsSet, goalNode) => { 
                        totalUpcomingGoalLevelsSet.add(goalNode.level);
                        return totalUpcomingGoalLevelsSet;
                    }, new Set());
                    
                    const actualLevel = (leaf.level + [...totalUpcomingGoalLevelsSet].length);
                    
                    return actualLevel <= highestDisplayedLevel;
                };
                
                const processLeaf = (previousDisplayedLeaf, leaf) => {
                    const isDisplayed = canDisplay(leaf);
                    
                    if(isDisplayed){
                        const mappedLeaf = vm.mapToDisplayedTouchPoint(leaf.node);

                        //We need to keep track only of the previous displayed leaf
                        //displayed leaves should be connected to each other as to not create gaps
                        mappedLeaf.from = previousDisplayedLeaf.node.id;
                        
                        //A node with skipped children should be clearly marked
                        //this can be done by means of painting stripes over them
                        mappedLeaf.stripey = leaf.children.reduce(
                            (stripey, childLeaf) => stripey || !canDisplay(childLeaf), 
                            false
                        );
                        
                        map.push(mappedLeaf);
                        
                        previousDisplayedLeaf = leaf;
                    }
                    
                    leaf.children.forEach(childLeaf => processLeaf(previousDisplayedLeaf, childLeaf));
                };
                
                //Root is a special case containing references to all goals, while not being a goal in itself
                //root should always be displayed, so simply ignore our 'process' function
                map.push(vm.mapToDisplayedTouchPoint(root.node));
                
                root.children.forEach(child => processLeaf(root, child));
                
                return map;
            },
            canIncreaseDisplayedTouchPoints(){
                return this.highestDisplayedTouchPointLevel < this.maxDisplayedTouchPoints;
            },
            canDecreaseDisplayedTouchPoints(){
                return this.highestDisplayedTouchPointLevel > MINIMUM_DISPLAYED_TOUCHPOINTS;
            },
            journeyUrl(){
                return api.getViewJourneyUrl(this.journeyId);
            }
        },
        data: () => ({
            highestDisplayedTouchPointLevel: DEFAULT_DISPLAYED_TOUCHPOINTS,
            journeyId: null,
            brickId: null,
            isLoading: false
        })
    }
</script>


<style scoped src="../../Style/Shared/tabbed-menu.scss" lang="scss">
</style>